import React, { useEffect, useMemo, useRef, useState } from "react";
import "./HeroSlider.css";

export default function HeroSlider() {
  const slides = useMemo(
    () => [
      {
        src: "./hero1.jpeg",
        headline: "Certified Residential Appraiser serving the Greater Puget Sound Region",
        sub: "Pierce • King • Thurston • Kitsap • Lewis • Mason • Snohomish • Grays Harbor",
        alt: "Washington-style newer construction Craftsman home",
      },
      {
        src: "./hero3.jpeg",
        headline: "Objective Valuations You Can Rely On",
        sub: "Lending • Estate • Divorce • Pre-Listing • Personal",
        alt: "Professional appraisal paperwork and tools on a clean desk",
      },
      {
        src: "./howthree.webp",
        headline: "Timely, Accurate, and Well-Supported Reports",
        sub: "FHA-approved • Single-Family Residence • Condominiums • Multi-Family • Sketching • Land Appraisals",
        alt: "Appraiser reviewing a report with a suburban Washington neighborhood in the background",
      },
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const trackRef = useRef(null);

  // Auto-play with cleanup
  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [slides.length, isPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % slides.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + slides.length) % slides.length);
      if (e.key === " ") {
        e.preventDefault();
        setIsPaused((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [slides.length]);

  const goToNext = () => setIndex((i) => (i + 1) % slides.length);
  const goToPrev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const goToSlide = (slideIndex) => setIndex(slideIndex);

  return (
    <section
      className="hero"
      aria-roledescription="carousel"
      aria-label="Featured appraisals slider"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="hero__viewport">
        {/* Navigation Arrows */}
        <button
          className="hero__nav hero__nav--prev"
          onClick={goToPrev}
          aria-label="Previous slide"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <button
          className="hero__nav hero__nav--next"
          onClick={goToNext}
          aria-label="Next slide"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Slides Track */}
        <div
          className="hero__track"
          ref={trackRef}
          style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
        >
          {slides.map((slide, i) => (
            <div
              className="hero__slide"
              key={i}
              aria-hidden={index !== i}
              aria-label={`Slide ${i + 1} of ${slides.length}`}
            >
              <div className="hero__image-container">
                <img
                  src={slide.src}
                  alt={slide.alt || ""}
                  className="hero__img"
                  loading={i === 0 ? "eager" : "lazy"}
                />
                <div className="hero__overlay" />
              </div>

              <div className="hero__content">
                <div className="hero__content-inner">
                  <h2 className="hero__headline">{slide.headline}</h2>
                  <p className="hero__sub">{slide.sub}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Slide Indicators */}
        <div className="hero__indicators" role="tablist" aria-label="Slide indicators">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`hero__indicator ${i === index ? "is-active" : ""}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === index}
              role="tab"
            >
              <span className="hero__indicator-progress" />
            </button>
          ))}
        </div>

        {/* Play/Pause Button */}
        <button
          className="hero__play-pause"
          onClick={() => setIsPaused(!isPaused)}
          aria-label={isPaused ? "Play slideshow" : "Pause slideshow"}
        >
          {isPaused ? (
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M5 3L15 10L5 17V3Z" fill="currentColor" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <rect x="6" y="3" width="2" height="14" fill="currentColor" />
              <rect x="12" y="3" width="2" height="14" fill="currentColor" />
            </svg>
          )}
        </button>

        {/* Slide Counter */}
        <div className="hero__counter" aria-hidden="true">
          <span className="hero__current">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="hero__total">/{String(slides.length).padStart(2, "0")}</span>
        </div>
      </div>
    </section>
  );
}